/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-underscore-dangle */
import { EditorApi, MonacoEditor } from '@idearoom/ir-code';
import { Dispatch } from 'redux';
import { ClientDataType } from '../constants/ClientDataType';
import { KEY_COLUMN } from '../constants/ClientDataColumn';
import { ClientDataBranch } from '../constants/ClientDataBranch';
import { UseFunLibraryReturn } from '../hooks/useFunLibrary';
import { TableData } from '../types/DataGrid';
import {
  setCreateCustomExpressionFunctionExpression,
  setCreateCustomExpressionFunctionOpen,
} from '../ducks/clientDataSlice';

const EXTRACT_TO_FUNCTION_ACTION = 'editor.action.extractToFunction';
const OPEN_FUNCTION_DEFINITION_ACTION = 'editor.action.openFunctionDefinition';
const MENU_SEPARATOR_ACTION = 'vs.actions.separator';

const POINTER_IS_ON_FUN_FUNCTION_ACTION_CONDITION = 'pointerIsOnFunFunction';
const HAS_VALID_SELECTION_CONDITION_ACTION_CONDITION = 'hasValidSelectionCondition';

const MENU_ACTIONS_TO_KEEP = [
  EXTRACT_TO_FUNCTION_ACTION, // Extract to Function
  OPEN_FUNCTION_DEFINITION_ACTION, // Open Function Definition
  MENU_SEPARATOR_ACTION, // Menu separator
  'editor.action.formatDocument', // Format Document
  'editor.action.formatSelection', // Format Selection
  'editor.action.clipboardCutAction', // Cut
  'editor.action.clipboardCopyAction', // Copy
  'editor.action.clipboardPasteAction', // Paste
  'editor.action.quickCommand', // Command Palette
];

export const removeUnusedMenuActions = (editor: EditorApi['editor']) => {
  const contextmenu = editor.getContribution('editor.contrib.contextmenu') as {
    _getMenuActions: (...args: unknown[]) => { id: string }[];
  } | null;

  if (contextmenu && '_getMenuActions' in contextmenu) {
    const reference = contextmenu._getMenuActions;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, func-names
    contextmenu._getMenuActions = function (...args: unknown[]) {
      const items: { id: string }[] = (typeof reference === 'function' && reference.apply(contextmenu, args)) || [];
      const filtered: { id: string }[] = [];

      let hasMenuItemAbove = false;
      items.forEach((item) => {
        // Only keep actions that we find relevant
        if (MENU_ACTIONS_TO_KEEP.some((keepId) => item.id.endsWith(keepId))) {
          // Only keep separators if there are other actions above it
          if (item.id.endsWith(MENU_SEPARATOR_ACTION)) {
            if (hasMenuItemAbove) {
              filtered.push(item);
            }
            hasMenuItemAbove = false;
          } else {
            hasMenuItemAbove = true;
            filtered.push(item);
          }
        }
      });

      return filtered;
    };
  }
};

const getReferenceCustomExpressionFunctionLink = (rowId: string) => {
  const url = new URL(
    '/portal/data/IdeaRoom/idearoom-reference:reference/main/customExpressionFunction',
    window.location.origin,
  );
  url.searchParams.set('startRowId', rowId);
  url.searchParams.set('endRowId', rowId);
  url.searchParams.set('columns', KEY_COLUMN);
  return url;
};

const getSupplierCustomExpressionFunctionLink = (
  groupId: string,
  clientId: string,
  clientDataType: ClientDataType,
  clientDataBranch: ClientDataBranch,
  rowId: string,
) => {
  const branch = clientDataType === ClientDataType.Supplier ? clientDataBranch : ClientDataBranch.Main;
  const url = new URL(
    `/portal/data/${groupId}/${clientId}:supplier/${branch}/customExpressionFunction`,
    window.location.origin,
  );
  url.searchParams.set('startRowId', rowId);
  url.searchParams.set('endRowId', rowId);
  url.searchParams.set('columns', KEY_COLUMN);
  return url;
};

const getFocusedFunFunctionKey = (
  editor: EditorApi['editor'],
  refFunctions: Map<string, TableData>,
  supFunctions: Map<string, TableData>,
) => {
  const position = editor.getPosition();
  const model = editor.getModel();
  if (model && position) {
    const absolutePosition = model.getOffsetAt(position);
    const v = model.getValue();
    const startIndex = v.substring(0, Math.min(absolutePosition + 4, v.length - 1)).lastIndexOf('fun.');
    if (startIndex !== -1) {
      const endIndex = v.indexOf('()', Math.max(absolutePosition - 2, 0));
      if (endIndex !== -1) {
        const funKey = v.substring(startIndex + 4, endIndex);
        if (refFunctions.has(funKey) || supFunctions.has(funKey)) {
          return funKey;
        }
      }
    }
  }
  return undefined;
};

const mapByKey = (entries: TableData[]): Map<string, TableData> =>
  entries.reduce((map, cur) => {
    map.set(cur[KEY_COLUMN], cur);
    return map;
  }, new Map());

export const addOpenFunctionDefinitionMenuAction = (
  editor: EditorApi['editor'],
  monaco: MonacoEditor,
  funLibData: UseFunLibraryReturn['data'],
  groupId: string,
  clientId: string,
  clientDataType: ClientDataType,
  clientDataBranch: ClientDataBranch,
) => {
  const refFunctions = mapByKey(funLibData[ClientDataType.Reference]);
  const supFunctions = mapByKey(funLibData[ClientDataType.Supplier]);

  const pointerIsOnFunFunction = editor.createContextKey<boolean>(POINTER_IS_ON_FUN_FUNCTION_ACTION_CONDITION, false);
  editor.onMouseDown(() => {
    pointerIsOnFunFunction.set(!!getFocusedFunFunctionKey(editor, refFunctions, supFunctions));
  });
  editor.onKeyDown(({ code }) => {
    if (code === 'MetaLeft' || code === 'MetaRight') {
      pointerIsOnFunFunction.set(!!getFocusedFunFunctionKey(editor, refFunctions, supFunctions));
    }
  });

  editor.addAction({
    id: OPEN_FUNCTION_DEFINITION_ACTION,
    label: 'Open Function Definition',
    contextMenuGroupId: 'navigation',
    precondition: POINTER_IS_ON_FUN_FUNCTION_ACTION_CONDITION,
    keybindings: [
      // eslint-disable-next-line no-bitwise
      monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyD,
    ],
    run: () => {
      const functionKey = getFocusedFunFunctionKey(editor, refFunctions, supFunctions);
      if (functionKey) {
        if (supFunctions.has(functionKey)) {
          window.open(
            getSupplierCustomExpressionFunctionLink(
              groupId,
              clientId,
              clientDataType,
              clientDataBranch,
              supFunctions.get(functionKey)!.rowId,
            ),
            '_blank',
          );
        } else if (refFunctions.has(functionKey)) {
          window.open(getReferenceCustomExpressionFunctionLink(refFunctions.get(functionKey)!.rowId), '_blank');
        }
      }
    },
  });
};

export const addExtractToFunctionMenuAction = (
  editor: EditorApi['editor'],
  monaco: MonacoEditor,
  dispatch: Dispatch,
) => {
  const hasValidSelectionCondition = editor.createContextKey<boolean>(
    HAS_VALID_SELECTION_CONDITION_ACTION_CONDITION,
    false,
  );

  editor.onDidChangeCursorSelection((event) => {
    const { selection } = event;
    const { startColumn, endColumn, startLineNumber, endLineNumber } = selection;
    if (startColumn !== endColumn || startLineNumber !== endLineNumber) {
      const model = editor.getModel();
      if (model && !model.getValueInRange(selection).includes('fun.')) {
        hasValidSelectionCondition.set(true);
        return;
      }
    }
    hasValidSelectionCondition.set(false);
  });

  editor.addAction({
    id: EXTRACT_TO_FUNCTION_ACTION,
    label: 'Extract to Function',
    contextMenuGroupId: 'navigation',
    precondition: HAS_VALID_SELECTION_CONDITION_ACTION_CONDITION,
    keybindings: [
      // eslint-disable-next-line no-bitwise
      monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyE,
    ],
    run: (e) => {
      const model = e.getModel();
      const selection = e.getSelection();
      if (model && selection) {
        const expression = model.getValueInRange(selection).trim();
        if (expression) {
          dispatch(setCreateCustomExpressionFunctionExpression(expression));
          dispatch(setCreateCustomExpressionFunctionOpen(true));
        }
      }
    },
  });
};
